import { Modal, Spinner } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { useAuditSession } from '../hooks/useAuditLog';
import { EventPill } from './auditFormatting';
import { formatEventTime, formatDuration, describeUserAgent, eventDetail } from './auditFormat';
import styles from '../pages/AgencyAuditLogPage.module.css';

interface AuditSessionModalProps {
  sessionId: string | null;
  onClose: () => void;
}

/**
 * The full trail for one sign-in: who, from where, on what, and every page
 * they opened in order. Opened by clicking any row in the audit log.
 */
export function AuditSessionModal({ sessionId, onClose }: AuditSessionModalProps) {
  const { data: session, isLoading, isError } = useAuditSession(sessionId);

  return (
    <Modal isOpen={!!sessionId} onClose={onClose} title="Session detail" size="lg" variant="side">
      {isLoading && <Spinner />}

      {isError && (
        <p className={patterns.cellMuted}>
          This session could not be loaded. It may have passed the retention window.
        </p>
      )}

      {session && (
        <>
          <div className={styles.sessionMeta}>
            <div>
              <span className={styles.metaLabel}>User</span>
              <span className={styles.metaValue}>
                {session.userName ?? session.email}
                {session.isAgency && (
                  <>
                    {' '}
                    <span className={patterns.pillAccent}>AGENCY</span>
                  </>
                )}
              </span>
            </div>
            <div>
              <span className={styles.metaLabel}>Email</span>
              <span className={styles.metaValue}>{session.email}</span>
            </div>
            <div>
              <span className={styles.metaLabel}>Practice</span>
              <span className={styles.metaValue}>{session.practiceName ?? '—'}</span>
            </div>
            <div>
              <span className={styles.metaLabel}>IP address</span>
              <span className={styles.metaValueMono}>{session.ipAddress ?? '—'}</span>
            </div>
            <div>
              <span className={styles.metaLabel}>Location</span>
              <span className={styles.metaValue}>{session.location ?? '—'}</span>
            </div>
            <div>
              <span className={styles.metaLabel}>Device</span>
              <span className={styles.metaValue}>
                {describeUserAgent(session.userAgent) ?? '—'}
              </span>
            </div>
            <div>
              <span className={styles.metaLabel}>Duration</span>
              <span className={styles.metaValue}>{formatDuration(session.durationSeconds)}</span>
            </div>
            <div>
              <span className={styles.metaLabel}>Pages viewed</span>
              <span className={styles.metaValue}>{session.pageViewCount}</span>
            </div>
            <div>
              <span className={styles.metaLabel}>Session ID</span>
              <span className={styles.metaValueMono}>{session.sessionId}</span>
            </div>
          </div>

          <h4 className={styles.trailTitle}>
            Trail — {session.trail.length} event{session.trail.length === 1 ? '' : 's'}
          </h4>

          <div className={styles.trail}>
            {session.trail.map((event) => {
              const { time } = formatEventTime(event.createdAt);
              const detail = eventDetail(event);
              const isAuthEvent = event.eventType !== 'page_view';

              return (
                <div
                  key={event.id}
                  className={`${styles.trailItem} ${isAuthEvent ? styles.trailItemAuth : ''}`}
                >
                  <span className={styles.trailTime}>{time}</span>
                  <div>
                    <span className={styles.trailPath}>{detail.primary}</span>
                    {isAuthEvent ? (
                      <>
                        {' '}
                        <EventPill eventType={event.eventType} />
                      </>
                    ) : (
                      detail.secondary && (
                        <span className={styles.trailSubPath}>{detail.secondary}</span>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
}
